/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import CardComponent from "pages/Front/Booking/components/CardComponent";
import ButtonSubmit from "pages/Front/Booking/components/ButtonSubmit";
import {
    CURRENCY,
    DEFAULT_HALL,
    EMAIL_LOGIN_REQUIRED,
    EMAIL_VERIFICATION_REQUIRED,
    MIN_COUNT_FOR_INQUIRY,
    SHOW_NOTIFICATION_METHODS
} from "helpers/config";
import {HallCode} from "types/enums/HallCode";
import BookingAction from "store/booking/BookingAction";
import {
    consoleLog,
    emailValidation,
    getNewStep,
    phoneValidation,
    redirectToReservationInquired,
    translate
} from "utils/utility";
import {Company} from "types/entities/Company";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {useDispatch} from "useDispatch";
import {useTypedSelector} from "helpers/reducers";
import ReservationAction from "store/reservations/ReservationAction";
import {PackageCategory} from "types/enums/PackageCategory";
import {ThunkAction} from "model/store/thunk";
import {toast} from "react-toastify";
import {RouteComponentProps, withRouter} from "react-router-dom";
import Loader from "components/Loader";
import {PackagesCartItem} from "types/entities/PackagesCartItem";
import {PackageItemItemType} from "types/entities/PackageItem";
import {CompanySchema} from "../../Schemas/companySchema";
import {AddressMandatorySchema} from "../../Schemas/adressSchema";
import CompanyForm from "../CompanyForm";
import {FieldErrors} from "react-hook-form/dist/types/errors";
import {Input} from "../BookingInput";
import {XMarkIcon} from "@heroicons/react/24/solid";
import {VerificationType} from "types/enums/VerificationType";
import {CompanyInputs} from "types/entities/specific/CompanyInputs";
import {captureCustomEvent} from "utils/sentryUtils";
import CardLabel from "./CardLabel";
import BookedExtraItems from "pages/Front/Booking/components/BookingCustomer/BookedExtraItems";
import VerificationCard from "pages/Front/Booking/components/BookingCustomer/VerificationCard";
import {ArboAddressMandatorySchema} from "pages/Front/Booking/Schemas/arboAddressSchema";
import BookedTimeslots from "pages/Front/Booking/components/BookingCustomer/BookedTimeslots";
import {useReactGoogleAnalytics} from "utils/useReactGoogleAnalytics";
import {DataLayerItem} from "types/entities/specific/DataLayerItem";
import {GAEventName} from "types/entities/specific/GAEventName";
import {countryList, PhoneCountry} from "utils/countryUtils";
import * as yup from "yup";
import "yup-phone-lite";
import {CountryCode} from "libphonenumber-js";
import {PhoneNumberType, PhoneNumberUtil} from "google-libphonenumber";
import {getPackageCategoryName} from "utils/packageUtils";
import LoginCardForShop from "pages/Front/Booking/components/BookingCustomer/LoginCardForShop";

export enum CustomerType {
    Private = "Private",
    Business = "Business",
}

interface BookingCustomerFormProps extends RouteComponentProps {
    company: Company | null;
    children?: React.ReactNode | React.ReactNode[];
}

const defaultPhoneCountryTextCode = "CH";

const BookingCustomerForm = (props: BookingCustomerFormProps) => {
    const dispatch = useDispatch();
    const [openAlertTOS, setOpenAlertTOS] = useState(false);
    const packagesCart = useTypedSelector(state => state.bookingState.bookingProcess.packageCart);
    const selectedPackageItems = useTypedSelector(state => state.bookingState.bookingProcess.selectedPackageItems);
    const packages = useTypedSelector(state => state.bookingState.packages);

    const bookingState = useTypedSelector(state => state.bookingState);
    const bookingFilter = useTypedSelector(state => state.bookingState.bookingProcess.filter);
    const emailVerified = EMAIL_VERIFICATION_REQUIRED ? useTypedSelector(state => state.bookingState.bookingProcess.emailVerified) : true;
    const {sendGenericEvent} = useReactGoogleAnalytics();

    const selectedPackage = useTypedSelector(state => state.bookingState.bookingProcess.package);
    const selectedCategory: PackageCategory = (selectedPackage?.category as PackageCategory) || bookingFilter.category;
    const packagesCartItemsOutstanding =
        packagesCart?.packagesCartItems &&
        packagesCart?.packagesCartItems.filter((packagesCartItem: PackagesCartItem) => {
            if (packagesCartItem.packageItem?.type === PackageItemItemType.BasePrice) {
                return false;
            }
            if (packagesCartItem.packageItem?.activity_id === null) {
                return true;
            }
        });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedCustomerType, setSelectedCustomerType] = useState<CustomerType>(CustomerType.Private);
    const [verificationType, setVerificationType] = useState(VerificationType.Email);
    const setStateHandler = state => {
        return new Promise(resolve => {
            setIsSubmitting(state);
            resolve(true);
            return;
        });
    };

    const setOpenTOSHandler = (setOpen: boolean) => {
        if (!setOpen) {
            setValue("tos_verifier", false);
        }
        setOpenAlertTOS(setOpen);
    };
    const defaultValues = props.company
        ? {
              ...(props.company || {}),
              state: (props.company && props.company.state) || "Switzerland",
              is_newsletter_enabled: true,
              name: (props.company && props.company.name) || "",
              company_type: false,
              required_on_package: !(selectedPackage && selectedPackage.category === PackageCategory.General) || true,
          }
        : {
              ...{},
              is_newsletter_enabled: true,
              company_type: false,
              tos: false,
          };
    const defaultCountry = countryList.find(item => item.textCode === defaultPhoneCountryTextCode);
    const defaultPhoneCountry: PhoneCountry = defaultCountry ? defaultCountry : countryList[161];
    const [selectedCountry, setSelectedCountry] = useState<PhoneCountry>(defaultPhoneCountry ? defaultPhoneCountry : countryList[0]);

    const {t} = useTranslation();
    //@ts-ignore
    const schema = DEFAULT_HALL === HallCode.Zwolle ? CompanySchema : DEFAULT_HALL === HallCode.ArboPark ? ArboAddressMandatorySchema : AddressMandatorySchema;
    const phoneUtil = PhoneNumberUtil.getInstance();
    const updatedSchema = schema.shape({
        phone: yup
            .string()
            .phone(selectedCountry?.textCode as CountryCode, t("Phone must be a valid phone number for region ") + selectedCountry?.name)
            .test("startsWithPlus", t("The phone number must start with the country code ") + selectedCountry?.code, function (value) {
                return (value || "").startsWith(selectedCountry?.code);
            })
            .test(
                "startsWithPlusAndGoogleVerify",
                t("Bad phone number format. The phone number must be mobile with international prefix ") + selectedCountry?.code,
                function (value) {
                    let phoneNumber;
                    try {
                        phoneNumber = phoneUtil.parseAndKeepRawInput(value, selectedCountry?.textCode);
                    } catch (err) {
                        return false;
                    }
                    return phoneUtil.isValidNumber(phoneNumber) && phoneUtil.getNumberType(phoneNumber) === PhoneNumberType.MOBILE;
                }
            )
            .test("mobileNumberBadFormatCH", t("Bad number format"), function (value) {
                if (selectedCountry?.textCode === "CH") {
                    return (value || "").startsWith(selectedCountry?.code + "7");
                }
                return true;
            })
            .required(),
    });
    const {
        clearErrors,
        control,
        register,
        handleSubmit,
        setValue,
        setError,
        formState: {errors},
    } = useForm<CompanyInputs>({
        defaultValues: defaultValues,
        resolver: yupResolver(updatedSchema),
    });

    const checkDataForErrors = (data: FieldErrors[] | CompanyInputs) => {
        let errorCount = 0;
        Object.values(data).forEach(value => {
            if (value.message) {
                errorCount += 1;
            }
            errorCount += 0;
        });
        return errorCount === 0;
    };

    const setValueHandler = (name, value) => {
        if (name === "tos_verifier") {
            if (openAlertTOS) {
                setValue(name, value);
            }
            setOpenAlertTOS(!openAlertTOS);
            return;
        }
        setOpenAlertTOS(false);
        setValue(name, value);
    };

    const submitCompanyError = async (data: any): Promise<boolean> => {
        consoleLog("submitCompanyError", data);
        return Promise.reject("submitCompanyError");
    };

    const submitCompany = async (data): Promise<boolean> => {
        toast.dismiss();
        setStateHandler(true);

        const checkFields = new Promise((resolve, reject) => {
            const isErrorFree = checkDataForErrors(data);
            if (isErrorFree) {
                return resolve(true);
            }
            return resolve(false);
        });

        const checkResult = async () => {
            try {
                const c = await checkFields
                    .then(res => {
                        return res;
                    })
                    .catch(err => {
                        return false;
                    });
                return c;
            } catch (e) {
                return false;
            }
        };
        const chr = await checkResult();
        if (chr) {
            if (!phoneValidation(data.phone)) {
                setError(
                    "phone",
                    {
                        type: "error",
                        message: "Invalid phone number",
                    },
                    {shouldFocus: true}
                );
                toast.error(t("Invalid phone number"));
                return Promise.reject(false);
            }

            if (!emailValidation(data.email)) {
                setError(
                    "email",
                    {
                        type: "error",
                        message: "Invalid email format",
                    },
                    {shouldFocus: true}
                );
                toast.error(t("Invalid email format"));
                return Promise.reject(false);
            }
        } else {
            const errorsArray = Object.keys({...data});
            if (errorsArray.length === 1 && data?.tos?.message == "Field must be checked") {
                setError(
                    "tos",
                    {
                        type: "error",
                        message: "You need to accept terms & conditions",
                    },
                    {shouldFocus: true}
                );
                toast.error(t("You need to accept terms & conditions"));
                return Promise.reject(false);
            }

            if (errorsArray.length >= 1 && data?.phone?.message == "phone must be at least 12 characters") {
                setError(
                    "phone",
                    {
                        type: "error",
                        message: "phone must be at least 12 characters",
                    },
                    {shouldFocus: true}
                );
                toast.error(t("phone must be at least 12 characters"));
                return Promise.reject(false);
            }

            if (errorsArray.length >= 1 && data?.tos_verifier?.message == "Field must be checked") {
                setError(
                    "tos_verifier",
                    {
                        type: "error",
                        message: t("You need to accept terms & conditions"),
                    },
                    {shouldFocus: true}
                );
                toast.error(t("You need to accept terms & conditions"));
                return Promise.reject(false);
            }
            toast.error(t("Please fill in all fields"));
            return Promise.reject(false);
        }

        if (data.company_type == false) {
            delete data.cin;
            delete data.name;
            delete data.company_name;
        }

        if (emailVerified) {
            data.email_verified = true;
        }

        if (packagesCart && data.reservation_note) {
            dispatch<ThunkAction<any, any>>(BookingAction.sendReservationNote(packagesCart, data.reservation_note)).then(r => {
                dispatch(BookingAction.pushReservationNote(r.reservationNote));
            });
        }
        return dispatch(BookingAction.fetchSetCustomer(data, packagesCart))
            .then(r => {
                dispatch(BookingAction.setCurrentNavigationStep(getNewStep(bookingState, 5)));
                return Promise.resolve(true);
            })
            .then(() => {
                setStateHandler(true);
                return Promise.resolve(true);
            });
    };

    const payNowHandler = fn => {
        fn();
        if (packagesCart) {
            dispatch<ThunkAction<any, any>>(BookingAction.fetchPayCart(packagesCart)).then(r => {});
            dispatch(BookingAction.setCurrentNavigationStep(getNewStep(bookingState, 6)));
        }
    };

    const inquireNowHandler = fn => {
        fn();
        if (packagesCart) {
            dispatch<ThunkAction<any, any>>(BookingAction.fetchCreateInquiry(packagesCart)).then(r => {
                dispatch(ReservationAction.setReservationResult(r.reservation));
                dispatch(BookingAction.resetReservationNote());
                toast.success(t("Reservation inquiry has been sent"));
                redirectToReservationInquired(props.history, packagesCart);
            });
            dispatch(BookingAction.setCurrentNavigationStep(getNewStep(bookingState, 6)));
        }
    };

    const verifyValueHandler = async (type: VerificationType): Promise<boolean> => {
        return new Promise(async (resolve, reject) => {
            return resolve(true);
        });
    };

    const submitMegaHandler = async (verifier: () => Promise<boolean>, submitter: (e?: React.BaseSyntheticEvent) => Promise<void>, callback) => {
        try {
            const verified = await verifier();
            try {
                verified;
            } catch (e) {
                captureCustomEvent(e as string);
                setStateHandler(false);
            }
            if (verified) {
                try {
                    const submitted = submitter();
                    submitted.then(
                        onResolved => {
                            sendGAData(GAEventName.AddShippingInfo);
                            return callback(() => {
                                dispatch(BookingAction.resetCustomTimeslot());
                                dispatch(BookingAction.setPossibleTimeslots([]));
                            });
                        },
                        onRejected => {
                            toast.error(t("Error in form"));
                            return;
                        }
                    );
                } catch (e) {
                    captureCustomEvent(e as string);
                    setStateHandler(false);
                }
            } else {
                return false;
            }
        } catch (e) {
            captureCustomEvent(e as string);
            setStateHandler(false);
            return false;
        }
    };

    const submitDummy = (data): Promise<void> => {
        return Promise.resolve();
    };

    const sendGAData = (eventName: GAEventName) => {
        if (!packagesCart) {
            return;
        }

        const items: DataLayerItem[] = packagesCart.packagesCartItems.map(packageItem => {
            const category = packages.find(packageS => packageS.id === packageItem.package_id);
            const item: DataLayerItem = {
                item_id: packageItem.package_id,
                item_name: packageItem?.packageItem
                    ? translate(packageItem.packageItem.name)
                    : getPackageCategoryName(category?.category ? category.category : ""),
                quantity: packageItem.drivers_count_adult + packageItem.drivers_count_child,
                item_variant: "",
                price: packageItem.price / (packageItem.drivers_count_adult + packageItem.drivers_count_child),
                currency: CURRENCY,
            };
            return item;
        });

        sendGenericEvent(eventName, packagesCart.price, items);
    };

    const setDefaultValuesFromCompany = (company: Company) => {
        setValue("id", company.id);
        setValue("first_name", company.first_name);
        setValue("last_name", company.last_name);
        setValue("email", company.email);
        setValue("phone", company.phone);
        setValue("street_address", company.street_address);
        setValue("city", company.city);
        setValue("postal_code", company.postal_code);
        setValue("name", company.name);
        setValue("company_type", company.name != "");
        setValue("state", company.state || "Switzerland");
        setValue("is_newsletter_enabled", company.is_newsletter_enabled);
    };

    useEffect(() => {
        sendGAData(GAEventName.BeginCheckout);
    }, []);

    useEffect(() => {
        if (props.company !== null) {
            setDefaultValuesFromCompany(props.company);
        }
    }, [props.company]);

    if (!packagesCart || (!selectedPackage && selectedPackageItems.length == 0)) {
        return <Loader />;
    }

    if (!packagesCart.packagesCartItemSlots) {
        return (
            <div className={"BookingRecapitulation"}>
                <CardComponent>{t("No timeslots chosen")}</CardComponent>
            </div>
        );
    }

    return (
        <div className={"BookingCustomerFormHolder w-full m-auto flex-wrap sm:flex-nowrap md:space-x-6"}>
            <div className={"BookingCustomerForm flex w-full"}>
                <div className="flex flex-col md:flex-row space-y-6 sm:gap-6 sm:space-y-0 justify-between flex-grow h-full columns-2">
                    <form
                        autoComplete="off"
                        className={"BookingCustomerForm flex flex-col md:flex-row space-y-6 sm:gap-6 sm:space-y-0 justify-between flex-grow h-full columns-2"}
                        name={"BookingCustomerForm"}
                        onSubmit={handleSubmit(submitDummy)}
                    >
                        <div className={"w-full md:basis-[50%] md:w-[50%] lg:basis-[45%] lg:w-[45%]"}>
                            <input {...register("id")} type="hidden" />
                            <input {...register("email_verified")} type="hidden" value={emailVerified ? 1 : 0} />
                            <input {...register("is_newsletter_enabled")} type="hidden" value={1} />

                            <input
                                type={"hidden"}
                                {...register("required_on_package")}
                                defaultValue={DEFAULT_HALL === HallCode.ArboPark ? 1 : selectedPackage?.category === PackageCategory.Event ? 1 : 0}
                            />
                            <input type={"hidden"} {...register("notification_method")} value={verificationType} />

                            <div className={"w-full flex flex-col space-y-6"}>
                                {EMAIL_VERIFICATION_REQUIRED && (
                                    <VerificationCard
                                        defaultValues={defaultValues}
                                        errors={errors}
                                        packagesCart={packagesCart}
                                        register={register}
                                        //@ts-ignore
                                        schema={updatedSchema}
                                        setError={setError}
                                        setValue={setValueHandler}
                                    />
                                )}

                                {EMAIL_LOGIN_REQUIRED && (
                                    <LoginCardForShop
                                        defaultValues={defaultValues}
                                        errors={errors}
                                        packagesCart={packagesCart}
                                        register={register}
                                        //@ts-ignore
                                        schema={updatedSchema}
                                        clearErrors={clearErrors}
                                        setError={setError}
                                        setValue={setValueHandler}
                                    />
                                )}
                                <CompanyForm
                                    clearError={clearErrors}
                                    control={control}
                                    defaultValues={defaultValues}
                                    errors={errors}
                                    origin={"customer"}
                                    register={register}
                                    //@ts-ignore
                                    schema={updatedSchema}
                                    selectedCountry={selectedCountry}
                                    selectedCustomerType={selectedCustomerType}
                                    setError={setError}
                                    setSelectedCountry={setSelectedCountry}
                                    setSelectedCustomerType={setSelectedCustomerType}
                                    setValue={setValueHandler}
                                />
                            </div>

                            {openAlertTOS && (
                                <div className={"fixed flex items-center justify-center inset-0 bg-gray-800/50 backdrop-blur-2xl z-20"}>
                                    <div className={"absolute bg-red-500 text-white p-4 max-w-sm z-100 m-4"}>
                                        <div className={"flex flex-row justify-between"}>
                                            <h2 className={"font-bold"}>{t("Terms and conditions")}</h2>
                                            <button className={"p-1 text-black"} onClick={() => setOpenTOSHandler(false)}>
                                                <XMarkIcon className={"w-5 h-5 ml-2 outline-2"} />
                                            </button>
                                        </div>
                                        <ul className={"my-4 flex flex-col space-y-2 list-disc pl-4 leading-4 border-b pb-6 border-white/20"}>
                                            <li>{t("If you do not show up, the ticket will expire.")}</li>
                                            <li>{t("No refund possible.")}</li>
                                            <li>{t("Arrive 15 minutes before the start of the race.")}</li>
                                        </ul>
                                        <label className={"flex flex-row items-center space-x-2 sm:space-x-4"} htmlFor={"tos"}>
                                            <Input
                                                className={"h-5 w-5"}
                                                errors={errors}
                                                label={""}
                                                labelLess={true}
                                                name={"tos"}
                                                register={register}
                                                type={"checkbox"}
                                                onChange={e => {
                                                    setValueHandler("tos", e.target.checked);
                                                }}
                                            />
                                            <span>{t("Accept")}</span>
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className={"w-full md:basis-[50%] md:w-[50%] lg:basis-[55%] lg:w-[55%]"}>
                            <div className={"w-full flex flex-col space-y-6"}>
                                {props.children}
                                <div className={"BookingRecapitulationCustomerForm flex flex-col mt-8 space-y-8 w-full"}>
                                    {packagesCart.packagesCartItemSlots && packagesCart.packagesCartItemSlots?.length > 0 && (
                                        <BookedTimeslots
                                            packagesCart={packagesCart}
                                            packagesCartItemsOutstanding={packagesCartItemsOutstanding}
                                            packagesCategory={selectedCategory}
                                        />
                                    )}

                                    {packagesCartItemsOutstanding && packagesCartItemsOutstanding?.length > 0 && (
                                        <BookedExtraItems packagesCartItemsOutstanding={packagesCartItemsOutstanding} />
                                    )}
                                    {SHOW_NOTIFICATION_METHODS && (
                                        <CardComponent>
                                            <div>
                                                <CardLabel title={t("Notifications")} />
                                                <p className="font-bold text-xs text-gray-800">{t("How do you prefer to receive notifications?")}</p>
                                                <fieldset className="mt-4">
                                                    <legend className="sr-only">Notification method</legend>
                                                    <div className="space-y-4">
                                                        {[
                                                            {title: "Phone (SMS)", type: VerificationType.Phone},
                                                            {title: "Email", type: VerificationType.Email},
                                                        ].map(verificationMethod => (
                                                            <div key={verificationMethod.type} className="flex items-center">
                                                                <input
                                                                    checked={verificationMethod.type === verificationType}
                                                                    className="h-4 w-4 rounded-md shadow-xs border-gray-800 focus:outline-none text-cta-800 focus:ring-cta-800 checked:bg-cta-800"
                                                                    disabled={!emailVerified}
                                                                    id={verificationMethod.type}
                                                                    name="notification-method"
                                                                    type="checkbox"
                                                                    onChange={() => setVerificationType(verificationMethod.type)}
                                                                />
                                                                <label
                                                                    className="ml-3 block text-sm font-medium leading-6 text-gray-900"
                                                                    htmlFor={verificationMethod.type}
                                                                >
                                                                    {verificationMethod.title}
                                                                </label>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </fieldset>
                                            </div>
                                        </CardComponent>
                                    )}

                                    {!isSubmitting ? (
                                        <div className={"space-y-8"}>
                                            {DEFAULT_HALL === HallCode.ArboPark && (
                                                <CardComponent>
                                                    {selectedCustomerType === CustomerType.Private ||
                                                    bookingFilter.children + bookingFilter.adults < MIN_COUNT_FOR_INQUIRY ? (
                                                        <div className={"flex flex-col space-y-1"}>
                                                            <CardLabel title={t("Your payment will be processed on the site of our payment provider")} />
                                                            <hr />
                                                            <div className={"flex flex-col pt-1 pb-3"}>
                                                                <div className={"flex flex-col max-w-xs m-auto w-full"}>
                                                                    <ButtonSubmit
                                                                        buttonType={"cta"}
                                                                        className={"rounded-md"}
                                                                        disabled={!emailVerified}
                                                                        icon={"card"}
                                                                        id={"ButtonPayNow"}
                                                                        label={t("Pay now")}
                                                                        onClick={() =>
                                                                            submitMegaHandler(
                                                                                () => verifyValueHandler(verificationType),
                                                                                handleSubmit(submitCompany, submitCompanyError),
                                                                                payNowHandler
                                                                            )
                                                                        }
                                                                    />
                                                                </div>
                                                                <img alt={"Worldline Logo"} className={"h-4 mt-4"} src={"/img/logo-worldline.svg"} />
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className={"flex flex-col space-y-1"}>
                                                            <CardLabel title={t("Your inquiry will be processed as soon as possible")} />
                                                            <hr />
                                                            <div className={"flex flex-col pt-1 pb-4"}>
                                                                <div className={"flex flex-col max-w-xs m-auto w-full"}>
                                                                    <ButtonSubmit
                                                                        buttonType={"cta"}
                                                                        className={"rounded-md"}
                                                                        disabled={!emailVerified}
                                                                        icon={"info"}
                                                                        id={"ButtonInquireNow"}
                                                                        label={t("Send inquiry")}
                                                                        onClick={() =>
                                                                            submitMegaHandler(
                                                                                () => verifyValueHandler(verificationType),
                                                                                handleSubmit(submitCompany, submitCompanyError),
                                                                                inquireNowHandler
                                                                            )
                                                                        }
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </CardComponent>
                                            )}
                                        </div>
                                    ) : (
                                        <div>
                                            <CardComponent>
                                                <div className={"flex flex-col space-y-1"}>
                                                    <em>{t("Payment status")}</em>
                                                    <hr />
                                                    <div className={"flex flex-col w-full justify-center items-center pt-1"}>
                                                        <span>{t("Processing")}</span>
                                                        <Loader naked />
                                                    </div>
                                                </div>
                                            </CardComponent>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
                <div id={"verification-code-container"}></div>
            </div>
        </div>
    );
};

export default withRouter(BookingCustomerForm);
